import React from 'react'
import LabeledValue from '../../../../components/common/LabeledValue'
import { TimeOffRequestDetails, isVacationBid } from '../../../../store/slices/Requestor/timeOffDetailsSlice'
import { getVacationBidAwareTableProps } from './vacationBidOverlapDisplay'
import RightPanel from './RightPanel'
import CurrentRequestSection from './CurrentRequestSection'
import HistorySection from './HistorySection'
import { formatDateToMMDDYY, formatDateTimeWithTimezone } from '../../../../utils/date'
import WithdrawSuccessBanner from './WithdrawSuccessBanner'
import { useProfileDisplay } from '../../../../hooks/useProfileDisplay'

interface WithdrawnViewProps {
  data: TimeOffRequestDetails
  tableRows: any[]
  highlightedDates: string[]
  handlePrint?: () => void
  showSuccessBanner?: boolean

}
// Prefixes "Requestor Proxy" when the actor's nuid differs from the request
// owner's. Not gated on manualReqRcvdDate — a proxy can resubmit/withdraw a
// request the employee originally submitted themselves, and vice versa.
const normalizeNuid = (value?: string | null) => (value ?? '').trim().toLowerCase()

const resolveOwnerNuid = (data: TimeOffRequestDetails): string =>
  data.employee?.empNuid || data.employee?.nuid || data.empNUID || data.nuid || ''

const normalizeDisplayName = (value?: string | null): string => {
  const withoutProxyPrefix = value?.replace(/^Requestor Proxy\s+/i, '') ?? ''
  const openParenIndex = withoutProxyPrefix.lastIndexOf('(')
  const name = openParenIndex >= 0 ? withoutProxyPrefix.slice(0, openParenIndex) : withoutProxyPrefix
  return name.trim().replaceAll(/\s+/g, ' ').toLowerCase()
}

const resolveOwnerName = (data: TimeOffRequestDetails): string =>
  normalizeDisplayName(
    data.employee?.empFullName ||
      data.employee?.fullName ||
      [data.employee?.firstName, data.employee?.middleName, data.employee?.lastName]
        .filter(Boolean)
        .join(' ') ||
      data.employeeName
  )

const compactNameParts = (entry: any): string =>
  [entry?.firstName, entry?.middleName, entry?.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ')

const resolveWithdrawnStatusEntry = (data: TimeOffRequestDetails): any =>
  data.statuses?.find((entry: any) => entry.status === 'WITHDRAWN') ?? data.statuses?.[0]

const appendMissingNuid = (value: string, nuid: string): string => {
  if (!value || !nuid || value.includes('(')) return value
  return `${value} (${nuid})`
}

const resolveWithdrawnByValue = (data: TimeOffRequestDetails): { value: string; nuid: string } => {
  const statusEntry = resolveWithdrawnStatusEntry(data)
  const withdrawnByNuid = data.withdrawnBy?.split('(')[1]?.replace(')', '').trim() || statusEntry?.updatedBy || data.updatedBy || ''
  const statusName = statusEntry?.middleName?.trim()
    ? compactNameParts(statusEntry)
    : (statusEntry?.fullName ?? '').trim()
  const withdrawnByValue = data.withdrawnBy?.includes('(')
    ? data.withdrawnBy
    : appendMissingNuid(statusName || data.withdrawnBy || '', withdrawnByNuid)

  return { value: withdrawnByValue, nuid: withdrawnByNuid }
}

const applyProxyPrefix = (data: TimeOffRequestDetails, value: string, valueNuid: string) => {
  const ownerNuid = normalizeNuid(resolveOwnerNuid(data))
  const actorNuid = normalizeNuid(valueNuid)
  const actorName = normalizeDisplayName(value)
  const ownerName = resolveOwnerName(data)
  const isManualProxyActor = Boolean(data.manualReqRcvdDate && actorName && ownerName && actorName !== ownerName)
  return actorNuid && ((ownerNuid && actorNuid !== ownerNuid) || (!ownerNuid && data.manualReqRcvdDate) || isManualProxyActor)
    ? `Requestor Proxy ${value}`
    : value
}

const getWithdrawnByDisplay = (data: TimeOffRequestDetails) => {
  if (!data.withdrawnBy) return ''

  const { value, nuid } = resolveWithdrawnByValue(data)

  return applyProxyPrefix(data, value, nuid)
}

const getCurrentDateDisplay = () => formatDateTimeWithTimezone(new Date().toISOString())

// Immediately after a withdraw action, callers may render this view with the
// pre-withdrawal `data` (no `withdrawnBy` yet), so fall back to the logged-in
// user's own profile (Redux) rather than `sessionStorage`, which never stores
// `userName`/`nuid` — that stale fallback previously echoed `data.submittedBy`
// (the original, possibly proxy, submitter) as the withdrawer. The same
// owner-vs-actor nuid check used for `data.withdrawnBy` is applied here so an
// actual proxy withdrawal is still labeled correctly before the data reload.
const getFallbackWithdrawnBy = (data: TimeOffRequestDetails, userInfo: string, userNuid: string) => {
  if (!userInfo) return data.submittedBy || ''

  const value = userNuid ? `${userInfo}${userNuid}` : userInfo
  const valueNuid = userNuid.replaceAll(/[()]/g, '').trim()

  return applyProxyPrefix(data, value, valueNuid)
}


/**
 * Renders the withdrawn request detail view.
 * @param data - The withdrawn request details.
 * @param tableRows - The current-request table rows.
 * @param highlightedDates - Dates highlighted in the request calendar.
 * @param handlePrint - Optional print handler.
 * @param showSuccessBanner - Whether to show the withdrawal success banner above the withdrawn details.
 * @returns The withdrawn request detail layout.
 */
const WithdrawnView: React.FC<WithdrawnViewProps> = ({ data, tableRows, highlightedDates, handlePrint, showSuccessBanner = false }) => {
  const { userInfo, userNuid } = useProfileDisplay()
  const fallbackWithdrawnBy = getFallbackWithdrawnBy(data, userInfo, userNuid)
  const withdrawnOn = data.withdrawnOn || (showSuccessBanner ? getCurrentDateDisplay() : '')
  const successWithdrawnBy = showSuccessBanner ? fallbackWithdrawnBy : ''
  const withdrawnBy = data.withdrawnBy ? getWithdrawnByDisplay(data) : successWithdrawnBy

  return (
  <div>
    {!showSuccessBanner && <h1 className="h1">Withdrawn Time Off Request</h1>}
    <div className="mt-12! flex flex-col mobile:gap-4 tablet:gap-6 desktop:gap-8 desktop:flex-row">
      <div className="flex-1">
        {showSuccessBanner && (
          <WithdrawSuccessBanner
            employeeName={data.employeeName}
            requestId={data.requestId}
            submittedOn={data.submittedOn}
          />
        )}
        {withdrawnOn && <div className="mb-4"><LabeledValue label="Withdrawn On" value={withdrawnOn} /></div>}
        {withdrawnBy && <div className="mb-4"><LabeledValue label="Withdrawn By" value={withdrawnBy} /></div>}
        {(data.requestRank != null || isVacationBid(data)) && <div className="mb-4"><LabeledValue label="Request Rank" value={data.requestRank == null ? 'NA' : String(data.requestRank)} /></div>}
        {data.manualReqRcvdDate && <div className="mb-4"><LabeledValue label="Manual Request Received Date" value={formatDateToMMDDYY(data.manualReqRcvdDate)} /></div>}

        {data.employeeComments && <div className="mb-4"><LabeledValue label="Employee Comments" value={data.employeeComments} /></div>}
      </div>
      <RightPanel data={data} variant="name-id" showSubmissionDetails handlePrint={handlePrint} />
    </div>
    <div className="mt-6!"><hr className="-variation-1" /></div>
    {data.history && data.history.length > 0 && <HistorySection history={data.history} />}
      <CurrentRequestSection {...getVacationBidAwareTableProps(tableRows, isVacationBid(data))} highlightedDates={highlightedDates} />
  </div>
  )
}

export default WithdrawnView
